import React, { FormEvent, useState } from 'react';
import api from '../services/api';
import MobileModal from './MobileModal';

const PASSWORD_HINT =
  'At least 12 characters, with uppercase, lowercase, a number, and a special character.';

const isStrongPassword = (value: string): boolean =>
  value.length >= 12 &&
  /[A-Z]/.test(value) &&
  /[a-z]/.test(value) &&
  /[0-9]/.test(value) &&
  /[^A-Za-z0-9]/.test(value);

interface ChangePasswordModalProps {
  isOpen: boolean;
  userId: number;
  onClose: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  isOpen,
  userId,
  onClose,
}) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
    setSuccess(false);
    setSubmitting(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('All fields are required.');
      return;
    }
    if (!isStrongPassword(newPassword)) {
      setError(PASSWORD_HINT);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match.');
      return;
    }

    try {
      setSubmitting(true);
      await api.patch(`/api/users/${userId}/password`, {
        currentPassword,
        newPassword,
      });
      setSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || 'Unable to change password.';
      setError(typeof message === 'string' ? message : 'Unable to change password.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MobileModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Change password"
      size="sm"
      footer={
        success ? (
          <button
            type="button"
            onClick={handleClose}
            className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
          >
            Done
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={handleClose}
              className="rounded-lg border border-monday-border px-4 py-2 text-sm font-medium text-monday-text hover:bg-monday-header"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="change-password-form"
              disabled={submitting}
              className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60"
            >
              {submitting ? 'Saving…' : 'Update password'}
            </button>
          </>
        )
      }
    >
      {success ? (
        <p className="text-sm text-monday-text">Password updated successfully.</p>
      ) : (
        <form id="change-password-form" onSubmit={handleSubmit} className="space-y-3">
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
          <div>
            <label
              htmlFor="current-password"
              className="block text-xs font-medium text-monday-text-muted mb-1"
            >
              Current password
            </label>
            <input
              id="current-password"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full rounded-lg border border-monday-border px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
            />
          </div>
          <div>
            <label
              htmlFor="new-password"
              className="block text-xs font-medium text-monday-text-muted mb-1"
            >
              New password
            </label>
            <input
              id="new-password"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full rounded-lg border border-monday-border px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
            />
            <p className="mt-1 text-xs text-monday-text-muted">{PASSWORD_HINT}</p>
          </div>
          <div>
            <label
              htmlFor="confirm-password"
              className="block text-xs font-medium text-monday-text-muted mb-1"
            >
              Confirm new password
            </label>
            <input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full rounded-lg border border-monday-border px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
            />
          </div>
        </form>
      )}
    </MobileModal>
  );
};

export default ChangePasswordModal;
